import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { snapshotsAPI, growGoalPromptsAPI, forumAttendanceAPI, usersAPI, eventsAPI, forumsAPI } from '../api/client';
import { useAuth } from '../context/useAuth';
import DeltaIndicator from '../components/DeltaIndicator';
import HorizontalScroll from '../components/HorizontalScroll';
import {
  Users,
  FileText,
  AlertCircle,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Minus,
  Loader2,
  Bell,
  Clock,
  Send,
  UserPlus,
  UserMinus,
  Plus,
  History,
  Download,
  X,
  Activity,
  Radar as RadarIcon,
  LineChart as LineChartIcon,
  ExternalLink,
  ClipboardList,
  SlidersHorizontal,
} from 'lucide-react';
import { ManageMemberById } from '../components/ManageMemberPanel';
import type {
  FacilitatorDashboardResponse,
  FacilitatorGroupData,
  FacilitatorMemberSummary,
  ForumHealthMetric,
  ForumHealthRadarPoint,
  ForumHealthTrendResponse,
  ForumHistoryResponse,
  MemberCategoryTrendResponse,
  ForumRoster,
  RosterMember,
  PaymentTrackingRow,
  PaymentReminderFields,
  UpcomingEvent,
} from '../types';
import {
  StateDistributionBar,
  DualMetricRadar,
  DualMetricCategoryTrendChart,
  currentYearMonth,
  formatYearMonth,
  previousYearMonth,
} from '../components/forumHealth';
import { confirmResendInvite, inviteSentLabel } from '../lib/inviteSent';

const ROW_ACTION =
  'inline-flex items-center gap-1 px-2 py-1 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-50 transition';

const PAYMENT_BADGE: Record<string, string> = {
  active: 'bg-green-100 text-green-700',
  trialing: 'bg-blue-100 text-blue-700',
  past_due: 'bg-amber-100 text-amber-700',
  pending: 'bg-amber-100 text-amber-700',
  unpaid: 'bg-gray-100 text-gray-600',
  canceled: 'bg-red-100 text-red-700',
  failed: 'bg-red-100 text-red-700',
};

function paymentStateLabel(member: PaymentTrackingRow): string {
  if (!member.requires_payment && !member.is_paying) return 'No payment required';
  return {
    active: 'Paid',
    // Not a free trial: card on file, first/next charge lands on the org
    // billing day. Members who already paid this month arrive as "active".
    trialing: 'Signed up — bills on billing day',
    pending: 'Awaiting confirmation',
    past_due: 'Past due',
    unpaid: 'Payment not started',
    canceled: 'Canceled',
    failed: 'Payment failed',
  }[member.payment_status] ?? member.payment_status.replace('_', ' ');
}

function formatRosterDate(value: string | null): string | null {
  if (!value) return null;
  const d = new Date(value);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

function rosterLastPaid(row: PaymentTrackingRow): string {
  const date = formatRosterDate(row.last_paid_at);
  if (!date) return '—';
  return row.last_paid_amount ? `$${row.last_paid_amount} · ${date}` : date;
}

function rosterNextCharge(row: PaymentTrackingRow): string {
  if (row.is_paused) return 'Paused';
  const amount = Number(row.monthly_amount);
  if (Number.isFinite(amount) && amount === 0) return '$0 · no charge';
  const date = formatRosterDate(row.next_charge_date);
  if (date) {
    if (row.cancel_at_period_end && row.payment_status !== 'canceled') {
      return `Ends ${date}`;
    }
    return row.payment_status === 'trialing' ? `First charge ${date}` : date;
  }
  if (!row.has_subscription) return 'Checkout not finished';
  return '—';
}

// YYYY-MM arithmetic for the history modal's default range.
function monthsBack(yearMonth: string, count: number): string {
  let result = yearMonth;
  for (let i = 0; i < count; i += 1) result = previousYearMonth(result);
  return result;
}

function nextYearMonth(yearMonth: string): string {
  const [y, m] = yearMonth.split('-').map(Number);
  const d = new Date(y, m, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function localYearMonth(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const EMPTY_EVENTS: UpcomingEvent[] = [];

interface PanelProps {
  forumId: number;
  roster: ForumRoster | null;
  payments: PaymentTrackingRow[];
  onInvite: () => void;
  onAddExisting: () => void;
  // Admin-only actions; omitted (undefined) for facilitators.
  onAddFacilitator?: () => void;
  onRemoveFacilitator?: (m: RosterMember) => void;
  onRemoveMember: (m: RosterMember) => void;
  onManageSaved?: () => void;
  nextEvent?: UpcomingEvent | null;
  forumEvents?: UpcomingEvent[];
}

// Operational per-forum dashboard, embedded in the unified /forum page for
// facilitators and admins: triage strip, at-a-glance stats, the Forum Roster
// table (attendance, snapshot, goals, payment), and the health charts.
// Contains named-member / individual data — kept cleanly separate from the
// anonymized org analytics panel.
export default function FacilitatorDashboardPanel({
  forumId,
  roster,
  payments,
  onInvite,
  onAddExisting,
  onAddFacilitator,
  onRemoveFacilitator,
  onRemoveMember,
  onManageSaved,
  nextEvent = null,
  forumEvents = EMPTY_EVENTS,
}: PanelProps) {
  const { user } = useAuth();
  const facilitatesThisForum = Boolean(
    user && (roster?.facilitators ?? []).some((person) => person.id === user.id),
  );
  const [managingId, setManagingId] = useState<number | null>(null);
  const [data, setData] = useState<FacilitatorDashboardResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [remindingMemberId, setRemindingMemberId] = useState<number | null>(null);
  const [resendingMemberId, setResendingMemberId] = useState<number | null>(null);
  const [sendingPaymentReminderId, setSendingPaymentReminderId] = useState<number | null>(null);
  const [paymentReminders, setPaymentReminders] = useState<Record<number, PaymentReminderFields>>({});
  const [inviteSent, setInviteSent] = useState<
    Record<number, Pick<FacilitatorMemberSummary, 'invite_sent_at' | 'invite_sent_by_name'>>
  >({});
  // Roster month filter: which month the Attended/Snapshot columns (and the
  // completion stats) are anchored to. Defaults to the current month.
  const [rosterMonth, setRosterMonth] = useState(currentYearMonth());
  const [showHistory, setShowHistory] = useState(false);
  // Radar filters. radarOverride starts from the radar baked into the
  // dashboard payload (current month, State only) until the first fetch of
  // both metrics completes. Both State and Direction are always shown side
  // by side now, so both are kept in state (no more single-metric toggle).
  const [radarMonth, setRadarMonth] = useState(currentYearMonth());
  const [radarOverride, setRadarOverride] = useState<Record<ForumHealthMetric, ForumHealthRadarPoint[] | null>>({
    state: null,
    direction: null,
  });
  const [radarLoading, setRadarLoading] = useState(false);
  const [hiddenCategories, setHiddenCategories] = useState<Set<string>>(new Set());
  // Chart view: Radar (spider) vs Line (trend over time), each of which can
  // show the Forum average or one member's own categories (to spot who's
  // struggling rather than just the averaged shape). Both State and
  // Direction are fetched/plotted together in every mode.
  const [chartViewMode, setChartViewMode] = useState<'radar' | 'line'>('radar');
  const [chartDataView, setChartDataView] = useState<'average' | 'member'>('average');
  const [trendMonths, setTrendMonths] = useState(6);
  const [trendData, setTrendData] = useState<Record<ForumHealthMetric, ForumHealthTrendResponse | null>>({
    state: null,
    direction: null,
  });
  const [trendLoading, setTrendLoading] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState<number | null>(null);
  const [memberTrendData, setMemberTrendData] = useState<Record<ForumHealthMetric, MemberCategoryTrendResponse | null>>({
    state: null,
    direction: null,
  });
  const [memberTrendLoading, setMemberTrendLoading] = useState(false);
  // Facilitator attendance checkboxes: {member_id: attended} for the
  // selected roster month.
  const [attendance, setAttendance] = useState<Record<string, boolean>>({});
  const [togglingAttendanceId, setTogglingAttendanceId] = useState<number | null>(null);
  const [rsvps, setRsvps] = useState<Record<string, string>>({});

  const activeGroup: FacilitatorGroupData | undefined = data?.groups.find(g => g.id === forumId);
  const isCurrentRosterMonth = rosterMonth === currentYearMonth();
  const isFutureRosterMonth = rosterMonth > currentYearMonth();
  const rsvpEvent = forumEvents.find(
    (e) => e.status !== 'cancelled' && localYearMonth(e.starts_at) === rosterMonth,
  ) ?? null;
  // Once this month's meeting is over, point at next month's RSVPs, which
  // are the ones facilitators are chasing.
  const nextMeetingMonth = nextEvent ? localYearMonth(nextEvent.starts_at) : null;
  const showNextMeetingShortcut = isCurrentRosterMonth
    && nextEvent !== null
    && nextMeetingMonth === nextYearMonth(rosterMonth);

  const loadDashboard = async (month?: string) => {
    const result = await snapshotsAPI.getFacilitatorDashboard(
      month && month !== currentYearMonth() ? month : undefined,
    );
    setData(result);
    return result;
  };

  const handleRemindMember = async (memberId: number) => {
    setRemindingMemberId(memberId);
    try {
      await growGoalPromptsAPI.remindMember(forumId, memberId);
      toast.success('Reminder sent');
    } catch {
      toast.error('Could not send the reminder');
    } finally {
      setRemindingMemberId(null);
    }
  };

  const handleResendInvite = async (member: FacilitatorMemberSummary) => {
    const sent = inviteSent[member.id] ?? member;
    if (!confirmResendInvite(member.name, sent.invite_sent_at, sent.invite_sent_by_name)) return;
    const memberId = member.id;
    setResendingMemberId(memberId);
    try {
      const result = await usersAPI.resendInvite(memberId);
      setInviteSent((prev) => ({
        ...prev,
        [memberId]: {
          invite_sent_at: result.invite_sent_at ?? new Date().toISOString(),
          invite_sent_by_name: result.invite_sent_by_name ?? null,
        },
      }));
      toast.success(result.message || 'Setup link re-sent');
    } catch {
      toast.error("Couldn't resend the invite");
    } finally {
      setResendingMemberId(null);
    }
  };

  const handleSendPaymentReminder = async (memberId: number) => {
    setSendingPaymentReminderId(memberId);
    try {
      const result = await forumsAPI.sendPaymentReminder(forumId, memberId);
      setPaymentReminders((prev) => ({ ...prev, [memberId]: result }));
      toast.success('Payment reminder sent');
    } catch (err) {
      toast.error(
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error
          ?? 'Could not send the payment reminder',
      );
    } finally {
      setSendingPaymentReminderId(null);
    }
  };

  const resetRadarFilters = () => {
    setRadarMonth(currentYearMonth());
    setRadarOverride({ state: null, direction: null });
    setRadarLoading(false);
    setHiddenCategories(new Set());
    setChartViewMode('radar');
    setChartDataView('average');
    setTrendData({ state: null, direction: null });
    setMemberTrendData({ state: null, direction: null });
    setSelectedMemberId(null);
  };

  const toggleRadarCategory = (category: string) => {
    setHiddenCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  // Line view (average): the forum's per-category values across recent
  // months, both metrics fetched in parallel so State and Direction can be
  // plotted together.
  useEffect(() => {
    if (chartViewMode !== 'line' || chartDataView !== 'average' || !activeGroup) return;
    let cancelled = false;
    setTrendLoading(true);
    Promise.all([
      snapshotsAPI.getForumHealthTrend(activeGroup.id, 'state', trendMonths),
      snapshotsAPI.getForumHealthTrend(activeGroup.id, 'direction', trendMonths),
    ])
      .then(([stateResult, directionResult]) => {
        if (!cancelled) setTrendData({ state: stateResult, direction: directionResult });
      })
      .catch(() => { if (!cancelled) toast.error("Couldn't load the Forum health trend"); })
      .finally(() => { if (!cancelled) setTrendLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartViewMode, chartDataView, activeGroup?.id, trendMonths]);

  // Default the by-member selection once a member becomes available, and
  // clear it if it's no longer offered (e.g. after switching forums).
  useEffect(() => {
    const available = activeGroup?.members.map((m) => m.id) ?? [];
    if (selectedMemberId && available.includes(selectedMemberId)) return;
    setSelectedMemberId(available[0] ?? null);
  }, [activeGroup, selectedMemberId]);

  // Line view (by member): one selected member's own values across every
  // category over recent months — the focused "who's struggling" trend,
  // both metrics fetched in parallel.
  useEffect(() => {
    if (chartViewMode !== 'line' || chartDataView !== 'member' || !activeGroup || !selectedMemberId) {
      return;
    }
    let cancelled = false;
    setMemberTrendLoading(true);
    Promise.all([
      snapshotsAPI.getMemberCategoryTrend(activeGroup.id, selectedMemberId, 'state', trendMonths),
      snapshotsAPI.getMemberCategoryTrend(activeGroup.id, selectedMemberId, 'direction', trendMonths),
    ])
      .then(([stateResult, directionResult]) => {
        if (!cancelled) setMemberTrendData({ state: stateResult, direction: directionResult });
      })
      .catch(() => { if (!cancelled) toast.error("Couldn't load the member's trend"); })
      .finally(() => { if (!cancelled) setMemberTrendLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartViewMode, chartDataView, activeGroup?.id, selectedMemberId, trendMonths]);

  // Radar view: both metrics for the chosen month, either forum-average or
  // one member's own ratings (the same average/member toggle as the line
  // view). Refetches on month, data-view, member, or forum changes.
  useEffect(() => {
    if (chartViewMode !== 'radar' || !activeGroup) return;
    const memberId =
      chartDataView === 'member' && selectedMemberId ? selectedMemberId : undefined;
    if (chartDataView === 'member' && !memberId) return;
    let cancelled = false;
    setRadarLoading(true);
    Promise.all([
      snapshotsAPI.getForumHealthRadar(activeGroup.id, radarMonth, 'state', memberId),
      snapshotsAPI.getForumHealthRadar(activeGroup.id, radarMonth, 'direction', memberId),
    ])
      .then(([stateResult, directionResult]) => {
        if (!cancelled) {
          setRadarOverride({
            state: stateResult.health_radar,
            direction: directionResult.health_radar,
          });
        }
      })
      .catch(() => {
        if (!cancelled) toast.error("Couldn't load the Forum health radar for those filters");
      })
      .finally(() => { if (!cancelled) setRadarLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartViewMode, chartDataView, activeGroup?.id, radarMonth, selectedMemberId]);

  // Attendance map for the selected roster month.
  useEffect(() => {
    let cancelled = false;
    forumAttendanceAPI.get(forumId, rosterMonth)
      .then((result) => { if (!cancelled) setAttendance(result.attendance); })
      .catch(() => { if (!cancelled) setAttendance({}); });
    return () => { cancelled = true; };
  }, [forumId, rosterMonth]);

  const rsvpEventKey = rsvpEvent?.occurrence_id ?? null;
  useEffect(() => {
    if (!rsvpEvent) {
      setRsvps({});
      return;
    }
    let cancelled = false;
    eventsAPI.getOccurrenceRsvps({
      eventId: rsvpEvent.id,
      scheduleId: rsvpEvent.schedule,
      date: rsvpEvent.occurrence_date,
    }).then((result) => {
      if (!cancelled) setRsvps(result.rsvps || {});
    }).catch(() => {
      if (!cancelled) setRsvps({});
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rsvpEventKey]);

  const handleToggleAttendance = async (memberId: number, nextAttended: boolean) => {
    const key = String(memberId);
    setAttendance((prev) => ({ ...prev, [key]: nextAttended }));
    setTogglingAttendanceId(memberId);
    try {
      await forumAttendanceAPI.toggle(forumId, memberId, nextAttended, rosterMonth);
    } catch {
      setAttendance((prev) => ({ ...prev, [key]: !nextAttended }));
      toast.error("Couldn't update attendance");
    } finally {
      setTogglingAttendanceId(null);
    }
  };

  // Initial load + reload when the roster month changes (the month re-anchors
  // snapshot columns and completion stats server-side).
  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    loadDashboard(rosterMonth > currentYearMonth() ? undefined : rosterMonth)
      .catch((error) => console.error('Error fetching facilitator dashboard:', error))
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rosterMonth]);

  // Switching forums (host page picker) clears chart filters and the month.
  useEffect(() => {
    resetRadarFilters();
    setRosterMonth(currentYearMonth());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forumId]);

  // Roster-derived rows: facilitators pinned first (with whatever stats they
  // have if they're also forum members), then members sorted by attention.
  const facilitatorIds = useMemo(
    () => new Set((roster?.facilitators ?? []).map((f) => f.id)),
    [roster],
  );
  const paymentById = useMemo(
    () => new Map(payments.map((p) => [p.id, { ...p, ...paymentReminders[p.id] }])),
    [payments, paymentReminders],
  );

  if (isLoading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy"></div>
      </div>
    );
  }

  if (!data || !activeGroup) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-400">
        <Users size={48} />
        <p className="mt-3 text-lg font-medium text-gray-600">No dashboard data yet</p>
        <p className="text-sm text-gray-400">
          Health and goal data will appear once this Forum has activity.
        </p>
      </div>
    );
  }

  // Per-forum grow-goal rollups, summed from the selected forum's members so
  // the KPI row is consistently scoped to the active forum (the payload's
  // commitment_activity block spans ALL of the facilitator's forums).
  const goalTotals = (activeGroup?.members ?? []).reduce(
    (acc, m) => ({
      active: acc.active + m.commitment_stats.active,
      completed_30d: acc.completed_30d + m.commitment_stats.completed_30d,
      overdue: acc.overdue + m.commitment_stats.overdue,
    }),
    { active: 0, completed_30d: 0, overdue: 0 },
  );

  // Triage lists: who needs chasing right now.
  const missingSnapshot = (activeGroup?.members ?? []).filter((m) => !m.has_current_snapshot);
  const needsGoal = (activeGroup?.members ?? []).filter((m) => m.needs_grow_goal);
  const declining = (activeGroup?.members ?? []).filter((m) => m.categories_declining.length > 0);

  // Members needing attention sort to the top of the roster table.
  const attentionScore = (m: FacilitatorMemberSummary) =>
    (m.has_current_snapshot ? 0 : 2) + (m.needs_grow_goal ? 1 : 0) + (m.categories_declining.length > 0 ? 1 : 0);
  const memberById = new Map(
    (activeGroup?.members ?? []).map((m) => [m.id, m]),
  );
  const roleById = new Map((roster?.members ?? []).map((m) => [m.id, m.role]));
  const attendedIds = Object.entries(attendance)
    .filter(([, attended]) => attended)
    .map(([id]) => Number(id))
    .filter((id) => roleById.has(id));
  const attendedGuests = attendedIds.filter((id) => roleById.get(id) === 'guest').length;
  const attendedMembers = attendedIds.length - attendedGuests;
  const sortedMembers = [...(activeGroup?.members ?? [])]
    .filter((m) => !facilitatorIds.has(m.id))
    .sort((a, b) => attentionScore(b) - attentionScore(a) || a.name.localeCompare(b.name));

  const formatNames = (members: FacilitatorMemberSummary[], max = 4) => {
    const names = members.map((m) => m.name.split(' ')[0]);
    if (names.length <= max) return names.join(', ');
    return `${names.slice(0, max).join(', ')} +${names.length - max} more`;
  };

  const isLineView = chartViewMode === 'line';
  const isMemberView = chartDataView === 'member';

  // Radar card derived state: filtered points (both metrics), chip
  // categories, series labels. In average view the State panel falls back to
  // the radar baked into the dashboard payload until the first both-metrics
  // fetch resolves; the member view has no baked-in fallback (the payload
  // radar is a forum average, which would read as the member's data).
  const radarStatePoints =
    radarOverride.state ?? (isMemberView ? null : activeGroup?.health_radar);
  const radarDirectionPoints = radarOverride.direction;
  const radarCategories = Array.from(new Set([
    ...(radarStatePoints?.map((p) => p.category) ?? []),
    ...(radarDirectionPoints?.map((p) => p.category) ?? []),
  ]));
  const radarVisibleStatePoints = radarStatePoints?.filter((p) => !hiddenCategories.has(p.category));
  const radarVisibleDirectionPoints = radarDirectionPoints?.filter((p) => !hiddenCategories.has(p.category));
  const allCategoriesHidden = radarCategories.length > 0
    && radarCategories.every((c) => hiddenCategories.has(c));
  // Whether either radar has anything to draw — used to collapse the tall
  // twin empty-radar placeholders into one compact line.
  const radarHasData = [
    ...(radarStatePoints ?? []),
    ...(radarDirectionPoints ?? []),
  ].some((p) => p.current_avg != null || p.previous_avg != null);
  const isRadarCurrentMonth = radarMonth === currentYearMonth();
  const radarCurrentLabel = isRadarCurrentMonth ? 'This month' : formatYearMonth(radarMonth);
  const radarPreviousLabel = isRadarCurrentMonth ? 'Last month' : formatYearMonth(previousYearMonth(radarMonth));

  // Line-view chip categories come from the trend response (average or
  // member, unioned across both metrics); the radar keeps using
  // radarCategories/hiddenCategories above. The same hide-chip mechanism now
  // applies to the by-member view too (it used to pick ONE category here).
  const averageTrendCategories = Array.from(new Set([
    ...(trendData.state?.categories.map((c) => c.category) ?? []),
    ...(trendData.direction?.categories.map((c) => c.category) ?? []),
  ]));
  const memberTrendCategories = Array.from(new Set([
    ...(memberTrendData.state?.categories.map((c) => c.category) ?? []),
    ...(memberTrendData.direction?.categories.map((c) => c.category) ?? []),
  ]));
  const chipCategories = !isLineView
    ? radarCategories
    : isMemberView ? memberTrendCategories : averageTrendCategories;
  const selectedMember = activeGroup?.members.find((m) => m.id === selectedMemberId);

  const getTrendIcon = (direction: string) => {
    if (direction === 'up') return <TrendingUp size={14} className="text-green-600" />;
    if (direction === 'down') return <TrendingDown size={14} className="text-red-600" />;
    return <Minus size={14} className="text-gray-400" />;
  };

  const renderPaymentCell = (personId: number, isFacilitator: boolean) => {
    const row = paymentById.get(personId);
    if (!row) {
      return isFacilitator ? (
        <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 whitespace-nowrap">
          Exempt
        </span>
      ) : (
        <span className="text-xs text-gray-400">—</span>
      );
    }
    return (
      <div className="space-y-1">
        <div className="flex items-center gap-1.5 flex-wrap">
          {row.is_paused && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
              Paused
            </span>
          )}
          {row.cancel_at_period_end && row.payment_status !== 'canceled' && (
            <span
              className="text-xs px-2 py-0.5 rounded-full bg-slate-800 text-white"
              title="Canceled in Stripe. They keep access until the period ends."
            >
              Leaving
            </span>
          )}
          {row.limit_app_access && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
              Limited access
            </span>
          )}
          {row.payment_issue ? (
            <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium whitespace-nowrap">
              Payment issue{row.amount_owed ? ` · $${row.amount_owed} owed` : ''}
            </span>
          ) : (
            <span
              className={`text-xs px-2 py-0.5 rounded-full capitalize ${
                PAYMENT_BADGE[row.payment_status] ?? 'bg-gray-100 text-gray-600'
              }`}
            >
              {paymentStateLabel(row)}
            </span>
          )}
        </div>
        {(row.is_paying
          || row.has_subscription
          || row.has_card
          || Boolean(row.last_paid_at)
          || row.requires_payment) && (
          <dl className="text-[11px] leading-4 text-gray-500 space-y-0.5">
            <div>
              Card{' '}
              <span className={row.has_card ? 'font-medium text-navy' : ''}>
                {row.has_card ? 'Yes' : 'No'}
              </span>
            </div>
            <div>
            Last{' '}
            <span className="text-gray-700">{rosterLastPaid(row)}</span>
            </div>
            <div>
              Next{' '}
              <span className="text-gray-700">{rosterNextCharge(row)}</span>
            </div>
          </dl>
        )}
        {row.payment_issue && !isFacilitator && (
          <div className="pt-0.5">
            <button
              type="button"
              onClick={() => handleSendPaymentReminder(personId)}
              disabled={sendingPaymentReminderId === personId || Boolean(row.payment_reminder_available_at)}
              title={
                row.payment_reminder_available_at
                  ? `You can send another on ${formatRosterDate(row.payment_reminder_available_at)}`
                  : 'Email and notify them to update their card on the website'
              }
              className="inline-flex items-center gap-1 px-2 py-1 border border-red-300 text-red-700 rounded-lg text-xs font-medium hover:bg-red-50 transition disabled:opacity-50 disabled:hover:bg-transparent whitespace-nowrap"
            >
              {sendingPaymentReminderId === personId ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <Send size={13} />
              )}
              Send payment reminder
            </button>
            {row.payment_reminder_sent_at && (
              <p className="text-[11px] text-gray-500 mt-0.5 whitespace-nowrap">
                Last sent {formatRosterDate(row.payment_reminder_sent_at)}
                {row.payment_reminder_sent_by ? ` by ${row.payment_reminder_sent_by}` : ''}
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

  const attendanceCell = (personId: number, name: string, inForum: boolean) => {
    if (!inForum || isFutureRosterMonth) return <span className="text-xs text-gray-400">—</span>;
    return (
      <input
        type="checkbox"
        checked={!!attendance[String(personId)]}
        onChange={(e) => handleToggleAttendance(personId, e.target.checked)}
        disabled={togglingAttendanceId === personId}
        aria-label={`${name} attended the ${formatYearMonth(rosterMonth)} Forum meeting`}
        className="w-4 h-4 rounded border-gray-300 text-navy focus:ring-navy/30 disabled:opacity-50"
      />
    );
  };

  const rsvpCell = (personId: number) => {
    const value = rsvps[String(personId)];
    if (!value) return <span className="text-xs text-gray-400">—</span>;
    return (
      <span className={`text-xs font-medium ${value === 'going' ? 'text-green-700' : 'text-gray-500'}`}>
        {value === 'going' ? 'Going' : value === 'not_going' ? 'Not going' : 'Maybe'}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Needs attention — the triage strip, first because it's the only part
          that demands action. Only rendered when something needs chasing. */}
      {(missingSnapshot.length > 0 || needsGoal.length > 0 || declining.length > 0) && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
          <h2 className="font-semibold text-navy flex items-center gap-2 mb-2">
            <AlertTriangle size={18} className="text-amber-500" />
            Needs attention
          </h2>
          <ul className="space-y-1.5 text-sm text-gray-700">
            {missingSnapshot.length > 0 && (
              <li>
                <span className="font-medium">{missingSnapshot.length} {missingSnapshot.length === 1 ? 'member hasn\u2019t' : 'members haven\u2019t'} submitted this month's snapshot:</span>{' '}
                {formatNames(missingSnapshot)}
              </li>
            )}
            {needsGoal.length > 0 && (
              <li>
                <span className="font-medium">{needsGoal.length} still {needsGoal.length === 1 ? 'owes' : 'owe'} a grow goal since the prompt:</span>{' '}
                {formatNames(needsGoal)} — use "Grow goal reminder" below.
              </li>
            )}
            {declining.length > 0 && (
              <li>
                <span className="font-medium">{declining.length} {declining.length === 1 ? 'member is' : 'members are'} declining in an area:</span>{' '}
                {formatNames(declining)}
              </li>
            )}
          </ul>
        </div>
      )}

      {/* Forum at a glance — the four KPIs and the State mix in one compact
          card (formerly a 4-card grid plus a separate full-width card). */}
      {activeGroup && (
        <div className="bg-white rounded-2xl p-5 shadow-sm">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-3 md:divide-x md:divide-gray-100">
            <div className="md:pl-4 md:first:pl-0">
              <p className="text-2xl font-bold text-navy">{activeGroup.members.length}</p>
              <p className="text-sm text-gray-500">Members</p>
            </div>
            <div className="md:pl-4">
              <p className="text-2xl font-bold text-navy">
                {activeGroup.snapshot_completion.percentage}%
              </p>
              <p className="text-sm text-gray-500">
                Snapshots ({activeGroup.snapshot_completion.submitted}/{activeGroup.snapshot_completion.total})
              </p>
              <div className="w-full max-w-[9rem] bg-gray-200 rounded-full h-1.5 mt-1.5">
                <div
                  className={`h-1.5 rounded-full transition-all ${
                    activeGroup.snapshot_completion.percentage >= 70
                      ? 'bg-green-500'
                      : activeGroup.snapshot_completion.percentage >= 40
                        ? 'bg-yellow-500'
                        : 'bg-red-500'
                  }`}
                  style={{ width: `${activeGroup.snapshot_completion.percentage}%` }}
                />
              </div>
            </div>
            <div className="md:pl-4">
              <p className="text-2xl font-bold text-navy">{goalTotals.active}</p>
              <p className="text-sm text-gray-500">Active Grow Goals</p>
            </div>
            <div className="md:pl-4">
              <p className={`text-2xl font-bold ${goalTotals.overdue > 0 ? 'text-red-500' : 'text-navy'}`}>
                {goalTotals.overdue}
              </p>
              <p className="text-sm text-gray-500">Overdue Grow Goals</p>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
              Health states
            </p>
            <StateDistributionBar dist={activeGroup.state_distribution} />
          </div>
        </div>
      )}

      {/* Forum Roster — the operational core: facilitators pinned on top,
          then members with attendance / snapshot / goals / payment, month
          filter, management actions, and the full-history sheet. */}
      {activeGroup && (
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-100 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div>
              <h2 className="font-semibold text-navy">Forum Roster</h2>
              {activeGroup.grow_goal_prompt ? (
                <p className="text-xs text-gray-500 mt-0.5">
                  Grow-goal prompt active ·{' '}
                  {activeGroup.grow_goal_prompt.missing_count === 0
                    ? 'everyone has set a goal'
                    : `${activeGroup.grow_goal_prompt.missing_count} still missing`}
                </p>
              ) : (
                <p className="text-xs text-gray-400 mt-0.5">
                  Grow goals are prompted automatically the morning after your Forum meets.
                </p>
              )}
              {!isFutureRosterMonth && (
                <p className="text-xs text-gray-600 mt-0.5">
                  Attended in {formatYearMonth(rosterMonth)}:{' '}
                  <span className="font-medium text-navy">
                    {attendedMembers} {attendedMembers === 1 ? 'member' : 'members'}
                    {' · '}
                    {attendedGuests} {attendedGuests === 1 ? 'guest' : 'guests'}
                  </span>
                </p>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <input
                type="month"
                value={rosterMonth}
                max={nextYearMonth(currentYearMonth())}
                onChange={(e) => { if (e.target.value) setRosterMonth(e.target.value); }}
                className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-navy/30"
                aria-label="Roster month"
              />
              <button
                onClick={() => setShowHistory(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 text-navy rounded-lg text-sm font-medium hover:bg-off-white transition"
              >
                <History size={15} />
                History
              </button>
              <button
                onClick={onAddExisting}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 text-navy rounded-lg text-sm font-medium hover:bg-off-white transition"
              >
                <Plus size={15} />
                Add existing
              </button>
              {onAddFacilitator && (
                <button
                  onClick={onAddFacilitator}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 text-navy rounded-lg text-sm font-medium hover:bg-off-white transition"
                >
                  <UserPlus size={15} />
                  Add facilitator
                </button>
              )}
              <button
                onClick={onInvite}
                className="inline-flex items-center gap-2 px-4 py-1.5 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy-dark transition"
              >
                <UserPlus size={15} />
                Invite member
              </button>
            </div>
          </div>
          {isFutureRosterMonth ? (
            <div className="px-4 py-2 bg-off-white text-xs text-gray-600 border-b border-gray-100">
              Looking ahead to {formatYearMonth(rosterMonth)} —{' '}
              {rsvpEvent
                ? `RSVPs are for the ${formatRosterDate(rsvpEvent.starts_at)} meeting.`
                : 'no meeting is scheduled that month.'}{' '}
              Attended and Snapshot fill in once the month starts.
            </div>
          ) : !isCurrentRosterMonth ? (
            <div className="px-4 py-2 bg-off-white text-xs text-gray-600 border-b border-gray-100">
              Viewing {formatYearMonth(rosterMonth)} — the Attended and Snapshot
              columns reflect that month; Trend, Grow Goals, and Payment show
              current state.
            </div>
          ) : showNextMeetingShortcut && nextEvent && nextMeetingMonth && (
            <div className="px-4 py-2 bg-off-white text-xs text-gray-600 border-b border-gray-100">
              Next meeting: {formatRosterDate(nextEvent.starts_at)} —{' '}
              <button
                type="button"
                onClick={() => setRosterMonth(nextMeetingMonth)}
                className="font-medium text-navy hover:underline pointer-coarse:py-2"
              >
                see RSVPs
              </button>
            </div>
          )}
          <HorizontalScroll>
            <table className="w-full stack-table">
              <thead className="bg-off-white">
                <tr>
                  <th className="px-2 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Member</th>
                  <th className="px-2 py-2.5 text-center text-xs font-medium text-gray-500 uppercase">
                    RSVP
                    {rsvpEvent && (
                      <span className="block normal-case font-normal text-gray-400">
                        {formatRosterDate(rsvpEvent.starts_at)}
                      </span>
                    )}
                  </th>
                  <th className="px-2 py-2.5 text-center text-xs font-medium text-gray-500 uppercase">Attended</th>
                  <th className="px-2 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Snapshot</th>
                  <th className="px-2 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Trend</th>
                  <th className="px-2 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Goals</th>
                  <th className="px-2 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Payment</th>
                  <th className="px-2 py-2.5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(roster?.facilitators ?? []).map((facilitator) => {
                  const stats = memberById.get(facilitator.id);
                  const inForum = stats != null;
                  return (
                    <tr key={`facilitator-${facilitator.id}`} className="bg-navy/[0.03] hover:bg-navy/[0.06] transition">
                      <td className="px-2 py-2.5">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-gold rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-white font-bold text-sm">
                              {facilitator.full_name.charAt(0)}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-navy">{facilitator.full_name}</p>
                            <span className="block text-[11px] font-semibold uppercase tracking-wide text-gold-dark">
                              Facilitator
                            </span>
                            <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                              <Link to={`/members/${facilitator.id}`} className={ROW_ACTION}>
                                <ExternalLink size={12} />
                                Profile
                              </Link>
                              {inForum && facilitatesThisForum && (
                                <Link
                                  to={`/forum/${forumId}/member/${facilitator.id}`}
                                  className={ROW_ACTION}
                                >
                                  <ClipboardList size={12} />
                                  Details
                                </Link>
                              )}
                              <button
                                type="button"
                                onClick={() => setManagingId(facilitator.id)}
                                className={ROW_ACTION}
                              >
                                <SlidersHorizontal size={12} />
                                Manage
                              </button>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td data-label="RSVP" className="px-2 py-2.5 text-center">
                        {rsvpCell(facilitator.id)}
                      </td>
                      <td data-label="Attended" className="px-2 py-2.5 text-center">
                        {attendanceCell(facilitator.id, facilitator.full_name, inForum)}
                      </td>
                      <td data-label="Snapshot" className="px-2 py-2.5">
                        {stats && !isFutureRosterMonth ? (
                          stats.has_current_snapshot ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 whitespace-nowrap">
                              <FileText size={12} />
                              Submitted
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-600 whitespace-nowrap">
                              <AlertCircle size={12} />
                              {stats.latest_snapshot_month ? `Last: ${stats.latest_snapshot_month}` : 'None yet'}
                            </span>
                          )
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                      <td data-label="Trend" className="px-2 py-2.5">
                        {stats ? (
                          <div className="flex items-center gap-1 whitespace-nowrap">
                            {getTrendIcon(stats.trend_direction)}
                            <span className="text-xs text-gray-500 capitalize">{stats.trend_direction}</span>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                      <td data-label="Goals" className="px-2 py-2.5">
                        {stats ? (
                          <div className="text-sm whitespace-nowrap">
                            <span className="text-gray-600">{stats.commitment_stats.active} active</span>
                            {stats.commitment_stats.overdue > 0 && (
                              <span className="text-red-500 font-medium"> · {stats.commitment_stats.overdue} overdue</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                      <td data-label="Payment" className="px-2 py-2.5">
                        {renderPaymentCell(facilitator.id, true)}
                      </td>
                      <td className="px-2 py-2.5 text-right">
                        {onRemoveFacilitator && (roster?.facilitators.length ?? 0) > 1 && (
                          <button
                            onClick={() => onRemoveFacilitator(facilitator)}
                            title={`Remove ${facilitator.full_name} as facilitator`}
                            aria-label={`Remove ${facilitator.full_name} as facilitator`}
                            className="p-1.5 text-gray-300 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                          >
                            <UserMinus size={15} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {sortedMembers.map((member: FacilitatorMemberSummary) => {
                  const rosterEntry = roster?.members.find((m) => m.id === member.id);
                  return (
                  <tr key={member.id} className="hover:bg-off-white/50 transition">
                    <td className="px-2 py-2.5 w-72">
                      <div className="flex items-start gap-2">
                        <div className="w-8 h-8 bg-navy rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-white font-bold text-sm">
                            {member.name.charAt(0)}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-navy">
                            {member.name}
                            {rosterEntry?.role === 'guest' && (
                              <span className="ml-1.5 align-middle inline-flex px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-sky-100 text-sky-700">
                                Guest
                              </span>
                            )}
                          </p>
                          <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                            <Link to={`/members/${member.id}`} className={ROW_ACTION}>
                              <ExternalLink size={12} />
                              Profile
                            </Link>
                            {facilitatesThisForum && (
                            <Link
                              to={`/forum/${forumId}/member/${member.id}`}
                              className={ROW_ACTION}
                              title="Snapshots, grow goals, prayers, and your notes"
                            >
                              <ClipboardList size={12} />
                              Details
                            </Link>
                            )}
                            <button
                              type="button"
                              onClick={() => setManagingId(member.id)}
                              className={ROW_ACTION}
                            >
                              <SlidersHorizontal size={12} />
                              Manage
                            </button>
                          </div>
                          {member.pending_setup && (
                            <span
                              className="mt-0.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 whitespace-nowrap"
                              title="Invited, but hasn't set up their account yet"
                            >
                              <Clock size={11} />
                              Invite pending
                            </span>
                          )}
                          {member.pending_setup && (() => {
                            const sent = inviteSent[member.id] ?? member;
                            const label = inviteSentLabel(sent.invite_sent_at, sent.invite_sent_by_name);
                            return label ? <p className="text-[11px] text-gray-500 mt-0.5">{label}</p> : null;
                          })()}
                          {member.categories_declining.length > 0 && (
                            <div className="flex items-start gap-1 mt-0.5">
                              <AlertTriangle size={12} className="text-amber-500 flex-shrink-0 mt-0.5" />
                              <span className="text-xs text-amber-600">
                                Declining: {member.categories_declining.join(', ')}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td data-label="RSVP" className="px-2 py-2.5 text-center">
                      {rsvpCell(member.id)}
                    </td>
                    <td data-label="Attended" className="px-2 py-2.5 text-center">
                      {attendanceCell(member.id, member.name, true)}
                    </td>
                    <td data-label="Snapshot" className="px-2 py-2.5">
                      {isFutureRosterMonth ? (
                        <span className="text-xs text-gray-400">—</span>
                      ) : member.has_current_snapshot ? (
                        <div className="flex items-center gap-2 whitespace-nowrap">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                            <FileText size={12} />
                            Submitted
                          </span>
                          {member.overall_delta !== null && (
                            <DeltaIndicator delta={Math.round(member.overall_delta * 10) / 10} size="sm" />
                          )}
                        </div>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-600 whitespace-nowrap">
                          <AlertCircle size={12} />
                          {member.latest_snapshot_month ? `Last: ${member.latest_snapshot_month}` : 'None yet'}
                        </span>
                      )}
                    </td>
                    <td data-label="Trend" className="px-2 py-2.5">
                      <div className="flex items-center gap-1 whitespace-nowrap">
                        {getTrendIcon(member.trend_direction)}
                        <span className="text-xs text-gray-500 capitalize">{member.trend_direction}</span>
                      </div>
                    </td>
                    <td data-label="Goals" className="px-2 py-2.5">
                      <div className="text-sm whitespace-nowrap">
                        <span className="text-gray-600">{member.commitment_stats.active} active</span>
                        {member.commitment_stats.overdue > 0 && (
                          <span className="text-red-500 font-medium"> · {member.commitment_stats.overdue} overdue</span>
                        )}
                      </div>
                      {member.needs_grow_goal ? (
                        <p className="text-xs text-red-600 mt-0.5 whitespace-nowrap">Owes a goal since the prompt</p>
                      ) : member.active_grow_goals.length > 0 ? (
                        <p className="text-xs text-gray-500 mt-0.5 truncate max-w-[9rem]" title={member.active_grow_goals.join('; ')}>
                          {member.active_grow_goals[0]}
                          {member.active_grow_goals.length > 1
                            ? ` +${member.active_grow_goals.length - 1} more`
                            : ''}
                        </p>
                      ) : null}
                    </td>
                    <td data-label="Payment" className="px-2 py-2.5">
                      {renderPaymentCell(member.id, false)}
                    </td>
                    <td className="px-2 py-2.5 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        {member.pending_setup && (
                          <button
                            onClick={() => handleResendInvite(member)}
                            disabled={resendingMemberId === member.id}
                            title="Email a fresh account-setup link (the old one may be lost or expired)"
                            className="inline-flex items-center gap-1 px-2 py-1 border border-gray-300 text-gray-600 rounded-lg text-xs font-medium hover:border-navy hover:text-navy transition disabled:opacity-50 whitespace-nowrap"
                          >
                            {resendingMemberId === member.id ? (
                              <Loader2 size={13} className="animate-spin" />
                            ) : (
                              <Send size={13} />
                            )}
                            Resend
                          </button>
                        )}
                        {member.needs_grow_goal && (
                          <button
                            onClick={() => handleRemindMember(member.id)}
                            disabled={remindingMemberId === member.id}
                            className="inline-flex items-center gap-1 px-2 py-1 border border-navy text-navy rounded-lg text-xs font-medium hover:bg-navy hover:text-white transition disabled:opacity-50 whitespace-nowrap"
                          >
                            {remindingMemberId === member.id ? (
                              <Loader2 size={13} className="animate-spin" />
                            ) : (
                              <Bell size={13} />
                            )}
                            Grow goal reminder
                          </button>
                        )}
                        {rosterEntry && (
                          <button
                            onClick={() => onRemoveMember(rosterEntry)}
                            title={`Remove ${member.name} from this Forum`}
                            aria-label={`Remove ${member.name} from this Forum`}
                            className="p-1.5 text-gray-300 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                          >
                            <UserMinus size={15} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </HorizontalScroll>
        </div>
      )}

      {/* Forum health radar/line (aggregate or per-member State/Direction per category) */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3 mb-3">
          <div>
            <h2 className="font-semibold text-navy flex items-center gap-2 mb-1">
              <Activity size={20} className="text-gold" />
              Forum Health
            </h2>
            <p className="text-sm text-gray-500">
              {isLineView
                ? isMemberView
                  ? `${selectedMember?.name ?? 'Member'}'s State & Direction by area, last ${trendMonths} months.`
                  : `Forum-average State & Direction by area, last ${trendMonths} months.`
                : isMemberView
                  ? `${selectedMember?.name ?? 'Member'}'s State & Direction by area, ${radarCurrentLabel.toLowerCase()} vs ${radarPreviousLabel.toLowerCase()}.`
                  : `Forum-average State & Direction by area, ${radarCurrentLabel.toLowerCase()} vs ${radarPreviousLabel.toLowerCase()}.`}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Radar vs Line view toggle */}
            <div className="flex rounded-lg border border-gray-200 overflow-hidden" role="group" aria-label="Chart view">
              <button
                onClick={() => setChartViewMode('radar')}
                aria-pressed={chartViewMode === 'radar'}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium transition ${
                  chartViewMode === 'radar' ? 'bg-navy text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                <RadarIcon size={14} /> Radar
              </button>
              <button
                onClick={() => setChartViewMode('line')}
                aria-pressed={chartViewMode === 'line'}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium transition ${
                  chartViewMode === 'line' ? 'bg-navy text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                <LineChartIcon size={14} /> Line
              </button>
            </div>

            {/* Forum average vs By member — applies to both radar and line. */}
            <div className="flex rounded-lg border border-gray-200 overflow-hidden" role="group" aria-label="Chart data source">
              {([
                { key: 'average' as const, label: 'Forum average' },
                { key: 'member' as const, label: 'By member' },
              ]).map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setChartDataView(opt.key)}
                  aria-pressed={chartDataView === opt.key}
                  className={`px-3 py-1.5 text-sm font-medium transition ${
                    chartDataView === opt.key
                      ? 'bg-navy text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {isMemberView && (
              <select
                value={selectedMemberId ?? ''}
                onChange={(e) => setSelectedMemberId(e.target.value ? Number(e.target.value) : null)}
                className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-navy/30"
                aria-label="Member"
              >
                {(activeGroup?.members ?? []).map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            )}
            {chartViewMode === 'radar' ? (
              <input
                type="month"
                value={radarMonth}
                min={activeGroup?.earliest_snapshot_month ?? undefined}
                max={currentYearMonth()}
                onChange={(e) => { if (e.target.value) setRadarMonth(e.target.value); }}
                className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-navy/30"
                aria-label="Radar month"
              />
            ) : (
              <select
                value={trendMonths}
                onChange={(e) => setTrendMonths(Number(e.target.value))}
                className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-navy/30"
                aria-label="Trend months"
              >
                {[3, 6, 12].map((n) => (
                  <option key={n} value={n}>Last {n} months</option>
                ))}
              </select>
            )}
          </div>
        </div>
        <div className="relative">
          {!isLineView && !radarLoading && !radarHasData ? (
            <div className="py-8 flex items-center justify-center">
              <p className="text-sm text-gray-400 italic text-center">
                No snapshots for {radarCurrentLabel.toLowerCase()} yet — the health radar will
                appear once {isMemberView ? (selectedMember?.name ?? 'this member') : 'members'}{' '}
                {isMemberView ? 'submits' : 'submit'}.
              </p>
            </div>
          ) : !isLineView && allCategoriesHidden ? (
            <div className="h-80 flex items-center justify-center">
              <p className="text-sm text-gray-400 italic">
                All areas hidden — turn an area back on below to see the radar.
              </p>
            </div>
          ) : !isLineView ? (
            <DualMetricRadar
              statePoints={radarVisibleStatePoints}
              directionPoints={radarVisibleDirectionPoints}
              currentLabel={radarCurrentLabel}
              previousLabel={radarPreviousLabel}
            />
          ) : isMemberView ? (
            <DualMetricCategoryTrendChart
              stateTrend={memberTrendData.state}
              directionTrend={memberTrendData.direction}
              hiddenCategories={hiddenCategories}
              emptyLabel={`No ratings yet for ${selectedMember?.name ?? 'this member'} — their categories will appear here.`}
            />
          ) : (
            <DualMetricCategoryTrendChart
              stateTrend={trendData.state}
              directionTrend={trendData.direction}
              hiddenCategories={hiddenCategories}
              emptyLabel="No snapshots yet for this period — the trend will appear here."
            />
          )}
          {(radarLoading || (isLineView && (isMemberView ? memberTrendLoading : trendLoading))) && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/60 rounded-xl">
              <Loader2 size={28} className="animate-spin text-navy" />
            </div>
          )}
        </div>
        {chipCategories.length > 0 && !(!isLineView && !radarLoading && !radarHasData) && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {chipCategories.map((cat) => {
              const hidden = hiddenCategories.has(cat);
              return (
                <button
                  key={cat}
                  onClick={() => toggleRadarCategory(cat)}
                  aria-pressed={!hidden}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium border transition ${
                    hidden
                      ? 'bg-gray-50 text-gray-400 border-gray-200 line-through'
                      : 'bg-navy/5 text-navy border-navy/20 hover:bg-navy/10'
                  }`}
                >
                  {cat}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {showHistory && (
        <ForumHistoryModal forumId={forumId} onClose={() => setShowHistory(false)} />
      )}
      {managingId != null && (
        <ManageMemberById
          memberId={managingId}
          onClose={() => setManagingId(null)}
          onSaved={() => onManageSaved?.()}
        />
      )}
    </div>
  );
}

// Full-history sheet: every member × month in a chosen range, with an Excel
// export. Lets facilitators review attendance, snapshots, goals, and payment
// in one place instead of paging the roster month by month.
function ForumHistoryModal({ forumId, onClose }: { forumId: number; onClose: () => void }) {
  const [start, setStart] = useState(() => monthsBack(currentYearMonth(), 5));
  const [end, setEnd] = useState(currentYearMonth());
  const [history, setHistory] = useState<ForumHistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    snapshotsAPI.getForumHistory(forumId, start, end)
      .then((result) => { if (!cancelled) setHistory(result); })
      .catch(() => { if (!cancelled) toast.error("Couldn't load the Forum history"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [forumId, start, end]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await snapshotsAPI.exportForumHistoryXlsx(forumId, start, end);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${(history?.forum_name ?? 'forum').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-history-${start}-to-${end}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Couldn't export the history sheet");
    } finally {
      setExporting(false);
    }
  };

  const paymentLabel = (cell: { payment_status: string | null; payment_paused: boolean | null }) => {
    if (!cell.payment_status) return '—';
    const label = cell.payment_status === 'active'
      ? 'paid'
      : cell.payment_status === 'trialing'
        ? 'signed up'
        : cell.payment_status.replace('_', ' ');
    return cell.payment_paused ? `${label} (paused)` : label;
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="forum-history-title"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 id="forum-history-title" className="text-lg font-semibold text-navy">
              Forum history
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Attendance, snapshots, grow goals, and payment for every member, month by month.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="month"
              value={start}
              max={end}
              onChange={(e) => { if (e.target.value) setStart(e.target.value); }}
              className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-700"
              aria-label="History start month"
            />
            <span className="text-sm text-gray-400">to</span>
            <input
              type="month"
              value={end}
              min={start}
              max={currentYearMonth()}
              onChange={(e) => { if (e.target.value) setEnd(e.target.value); }}
              className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-700"
              aria-label="History end month"
            />
            <button
              onClick={handleExport}
              disabled={exporting || loading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy-dark transition disabled:opacity-50"
            >
              {exporting ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
              Export to Excel
            </button>
            <button
              onClick={onClose}
              aria-label="Close history"
              className="p-1.5 text-gray-400 hover:text-navy rounded-lg transition"
            >
              <X size={18} />
            </button>
          </div>
        </div>
        <div className="overflow-auto flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={28} className="animate-spin text-navy" />
            </div>
          ) : !history || history.members.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-16">
              No history for this range yet.
            </p>
          ) : (
            <table className="w-full text-sm stack-table">
              <thead className="bg-off-white sticky top-0">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Member</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Month</th>
                  <th className="px-4 py-2.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Attended</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Snapshot</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Δ</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Goals</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {history.members.flatMap((member) =>
                  history.months.map((month, monthIdx) => {
                    const cell = member.months[month];
                    if (!cell) return null;
                    return (
                      <tr key={`${member.id}-${month}`} className="hover:bg-off-white/50">
                        <td className="px-4 py-2 whitespace-nowrap">
                          {monthIdx === 0 ? (
                            <span className="font-medium text-navy">
                              {member.name}
                              {member.is_facilitator && (
                                <span className="ml-1.5 text-[10px] font-semibold uppercase text-gold-dark">
                                  Facilitator
                                </span>
                              )}
                            </span>
                          ) : (
                            <span className="text-gray-300">·</span>
                          )}
                        </td>
                        <td data-label="Month" className="px-4 py-2 whitespace-nowrap text-gray-600">{formatYearMonth(month)}</td>
                        <td data-label="Attended" className="px-4 py-2 text-center">
                          {cell.attended === null ? (
                            <span className="text-gray-300">—</span>
                          ) : cell.attended ? (
                            <span className="text-green-600 font-medium">Yes</span>
                          ) : (
                            <span className="text-red-500">No</span>
                          )}
                        </td>
                        <td data-label="Snapshot" className="px-4 py-2 whitespace-nowrap">
                          {cell.snapshot_submitted ? (
                            <span className="text-green-600">Submitted</span>
                          ) : (
                            <span className="text-gray-400">Missing</span>
                          )}
                        </td>
                        <td data-label="Change" className="px-4 py-2 text-right text-gray-600">
                          {cell.overall_delta ?? '—'}
                        </td>
                        <td data-label="Goals" className="px-4 py-2 text-right whitespace-nowrap text-gray-600">
                          {cell.goals_active} active
                          {cell.goals_overdue > 0 && (
                            <span className="text-red-500"> · {cell.goals_overdue} overdue</span>
                          )}
                        </td>
                        <td data-label="Payment" className="px-4 py-2 whitespace-nowrap capitalize text-gray-600">
                          {paymentLabel(cell)}
                        </td>
                      </tr>
                    );
                  }),
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
